import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, XCircle, Star, Clock, MessageSquare, Pencil, Trash2 } from "lucide-react";
import { getUserFriendlyError } from "@/lib/error-handler";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminReviews() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"pending" | "all">("pending");

  const { data: reviews, isLoading } = useQuery({
    queryKey: ["admin-reviews", filter],
    queryFn: async () => {
      let query = supabase
        .from("reviews")
        .select("*, venues(name, slug), profiles!reviews_user_id_profiles_fkey(username, band_name, visible_name)")
        .order("created_at", { ascending: false });

      if (filter === "pending") {
        query = query.eq("status", "pending");
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status, reason }: { id: string; status: string; reason?: string }) => {
      const update: Record<string, any> = { status };
      if (reason) update.rejection_reason = reason;
      const { error } = await supabase.from("reviews").update(update).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-reviews"] });
      toast({ title: status === "approved" ? "Review aprobada" : "Review rechazada" });
      setRejectId(null);
      setRejectReason("");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: getUserFriendlyError(err), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Delete related data first
      await supabase.from("review_votes").delete().eq("review_id", id);
      await supabase.from("review_revisions").delete().eq("review_id", id);
      const { error } = await supabase.from("reviews").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-reviews"] });
      toast({ title: "Review eliminada" });
      setDeleteId(null);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: getUserFriendlyError(err), variant: "destructive" });
    },
  });

  const statusColors: Record<string, string> = {
    pending: "bg-warning/10 text-warning",
    approved: "bg-success/10 text-success",
    rejected: "bg-destructive/10 text-destructive",
  };
  const statusLabels: Record<string, string> = {
    pending: "Pendiente",
    approved: "Aprobada",
    rejected: "Rechazada",
  };

  return (
    <AdminLayout requiredModule="reviews" requiredPermission="can_view">
      <div>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold">Moderación de Reviews</h1>
            <p className="text-sm text-muted-foreground">
              {reviews?.length ?? 0} review{(reviews?.length ?? 0) !== 1 ? "s" : ""}
              {filter === "pending" ? " pendientes" : " en total"}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant={filter === "pending" ? "default" : "outline"} size="sm" onClick={() => setFilter("pending")}>
              <Clock className="mr-2 h-4 w-4" /> Pendientes
            </Button>
            <Button variant={filter === "all" ? "default" : "outline"} size="sm" onClick={() => setFilter("all")}>
              Todas
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-40" />)}</div>
        ) : !reviews?.length ? (
          <div className="py-16 text-center text-muted-foreground">
            <MessageSquare className="mx-auto mb-2 h-8 w-8 opacity-40" />
            <p>No hay reviews {filter === "pending" ? "pendientes" : ""}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => {
              const venue = review.venues as any;
              const profile = review.profiles as any;

              return (
                <Card key={review.id}>
                  <CardContent className="p-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{venue?.name ?? "Sala desconocida"}</span>
                          <Badge className={statusColors[review.status ?? "pending"]}>
                            {statusLabels[review.status ?? "pending"]}
                          </Badge>
                        </div>

                        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                          <span>Por: {profile?.visible_name || profile?.band_name || profile?.username || profile?.email || "Usuario"}</span>
                          <span>·</span>
                          <span>{new Date(review.created_at).toLocaleDateString("es-ES")}</span>
                          {review.event_date && (
                            <>
                              <span>·</span>
                              <span>Evento: {new Date(review.event_date).toLocaleDateString("es-ES")}</span>
                            </>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm">
                          <span className="flex items-center gap-1">
                            <Star className="h-3.5 w-3.5 fill-warning text-warning" />
                            General: {Number(review.overall_rating).toFixed(1)}
                          </span>
                          {review.sound_rating && <span>Sonido: {Number(review.sound_rating).toFixed(1)}</span>}
                          {review.staff_rating && <span>Staff: {Number(review.staff_rating).toFixed(1)}</span>}
                          {review.price_paid && <span>Precio: {review.price_paid}€</span>}
                        </div>

                        <div className="flex flex-wrap gap-2 text-xs">
                          {review.sound_tech !== null && (
                            <Badge variant="outline">{review.sound_tech ? "✓" : "✗"} Técnico sonido</Badge>
                          )}
                          {review.technical_rider !== null && (
                            <Badge variant="outline">{review.technical_rider ? "✓" : "✗"} Rider técnico</Badge>
                          )}
                          {review.catering !== null && (
                            <Badge variant="outline">{review.catering ? "✓" : "✗"} Catering</Badge>
                          )}
                          {review.merchandising_area !== null && (
                            <Badge variant="outline">{review.merchandising_area ? "✓" : "✗"} Merchandising</Badge>
                          )}
                        </div>

                        {review.rejection_reason && (
                          <p className="text-sm text-destructive">Motivo rechazo: {review.rejection_reason}</p>
                        )}
                      </div>

                      <div className="flex gap-2 sm:flex-col">
                        {review.status === "pending" && (
                          <>
                            <Button size="sm" onClick={() => statusMutation.mutate({ id: review.id, status: "approved" })} disabled={statusMutation.isPending}>
                              <CheckCircle className="mr-1 h-4 w-4" /> Aprobar
                            </Button>
                            <Button variant="outline" size="sm" className="text-destructive" onClick={() => setRejectId(review.id)} disabled={statusMutation.isPending}>
                              <XCircle className="mr-1 h-4 w-4" /> Rechazar
                            </Button>
                          </>
                        )}
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/admin/reviews/${review.id}/edit`}>
                            <Pencil className="mr-1 h-4 w-4" /> Editar
                          </Link>
                        </Button>
                        <Button variant="outline" size="sm" className="text-destructive" onClick={() => setDeleteId(review.id)}>
                          <Trash2 className="mr-1 h-4 w-4" /> Eliminar
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Reject dialog */}
        <Dialog open={!!rejectId} onOpenChange={(o) => { if (!o) { setRejectId(null); setRejectReason(""); } }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Rechazar Review</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Textarea placeholder="Motivo del rechazo…" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setRejectId(null); setRejectReason(""); }}>Cancelar</Button>
                <Button variant="destructive" onClick={() => { if (rejectId) statusMutation.mutate({ id: rejectId, status: "rejected", reason: rejectReason }); }} disabled={statusMutation.isPending}>
                  Confirmar Rechazo
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete dialog */}
        <Dialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Eliminar Review</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">
              Esta acción es irreversible. La review será eliminada permanentemente y se recalcularán los agregados de la sala.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteId(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={() => { if (deleteId) deleteMutation.mutate(deleteId); }} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? "Eliminando..." : "Eliminar definitivamente"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
